import twemoji from "twemoji";
import EasyMDE from "easymde";

import { isCommunicationForm, isMobile } from "./common/utils";
import {
    createFileInput,
    createMarkdownEditor,
    handleFormSubmissionShortcuts,
    imageUploadOptions,
} from "./common/markdown-editor";
import { getCollapsedCommentThreadsSet } from "./common/comments";
import { insertTableAction, attachTableTools } from "./common/table-tools";
import { toggleEmojiPicker } from "./common/emoji-picker";

const INITIAL_SYNC_DELAY = 50;

const App = {
    onCreate() {
        this.initializeThemeSwitcher();
        this.addTargetBlankToExternalLinks();
    },
    onMount() {
        this.initializeImageZoom();
        this.initializeEmojiForPoorPeople();
        this.blockCommunicationFormsResubmit();
        this.restoreCommentThreadsState();

        const registeredEditors = this.initializeMarkdownEditor();

        setTimeout(function () {
            registeredEditors.forEach((editor) => {
                // textarea value after navigation might be restored after codemirror inited
                if (editor.element.value && !editor.codemirror.getValue()) {
                    editor.codemirror.setValue(editor.element.value);
                }
            });
        }, INITIAL_SYNC_DELAY);
    },
    initializeEmojiForPoorPeople() {
        const isApple = /iPad|iPhone|iPod|OS X/.test(navigator.userAgent) && !window.MSStream;
        if (!isApple) {
            document.body = twemoji.parse(document.body, {
                base: "https://cdn.jsdelivr.net/gh/twitter/twemoji@14.0.2/assets/",
            });
        }
    },
    initializeThemeSwitcher() {
        const mediaQueryList = window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)");

        const setFaviconHref = (e) => {
            const svgFavicon = document.querySelector('link[type="image/svg+xml"]');
            const isDark = e.matches;
            svgFavicon.href = isDark ? "/static/images/favicon/favicon-dark.svg" : "/static/images/favicon/favicon.svg";
        };

        setFaviconHref(mediaQueryList);
        mediaQueryList.addListener(setFaviconHref);
    },
    /**
     * Initializes markdown editor with toolbar **for server-side rendered pages**
     * e.g. Create post page
     *
     * Simple editors are initialized in the `CommentMarkdownEditor` Vue component
     *
     * @returns {CodeMirrorEditor[]}
     */
    initializeMarkdownEditor() {
        if (isMobile()) return []; // we don't need fancy features on mobiles

        const fullMarkdownEditors = [...document.querySelectorAll(".markdown-editor-full")].reduce(
            (editors, element) => {
                const fileInputEl = createFileInput({ allowedTypes: imageUploadOptions.allowedTypes });
                const editor = createMarkdownEditor(element, {
                    autosave: {
                        enabled: false,
                    },
                    hideIcons: ["preview", "side-by-side", "fullscreen", "guide"],
                    showIcons: ["heading-2", "code"],
                    toolbar: [
                        {
                            name: "bold",
                            action: EasyMDE.toggleBold,
                            className: "fa fa-bold",
                            title: "Bold",
                        },
                        {
                            name: "italic",
                            action: EasyMDE.toggleItalic,
                            className: "fa fa-italic",
                            title: "Italic",
                        },
                        {
                            name: "strikethrough",
                            action: EasyMDE.toggleStrikethrough,
                            className: "fa fa-strikethrough",
                            title: "Strikethrough (~~text~~)",
                        },
                        {
                            name: "header",
                            action: EasyMDE.toggleHeadingSmaller,
                            className: "fas fa-heading",
                            title: "Heading",
                        },
                        {
                            name: "quote",
                            action: EasyMDE.toggleBlockquote,
                            className: "fas fa-quote-right",
                            title: "Quote",
                        },
                        "|",
                        {
                            name: "list",
                            action: EasyMDE.toggleUnorderedList,
                            className: "fas fa-list",
                            title: "List",
                        },
                        {
                            name: "url",
                            action: EasyMDE.drawLink,
                            className: "fas fa-link",
                            title: "Insert URL",
                        },
                        {
                            name: "image",
                            action: EasyMDE.drawImage,
                            className: "fas fa-image",
                            title: "Insert an image",
                        },
                        {
                            name: "upload-file",
                            action: () => {
                                fileInputEl.click();
                            },
                            className: "fa fa-paperclip",
                            text: "Upload image",
                            title: "Upload image",
                        },
                        {
                            name: "code",
                            action: EasyMDE.toggleCodeBlock,
                            className: "fas fa-code",
                            title: "Insert code",
                        },
                        {
                            name: "table",
                            action: insertTableAction,
                            className: "fas fa-table",
                            title: "Вставить таблицу",
                        },
                        {
                            name: "emoji",
                            // `editor` объявлен ниже по коду, но стрелка
                            // исполняется только по клику — к этому моменту
                            // он давно инициализирован.
                            action: () => toggleEmojiPicker(editor),
                            className: "far fa-smile",
                            title: "Emoji",
                        },
                    ],
                });

                editor.element.form.addEventListener("keydown", handleFormSubmissionShortcuts);
                inlineAttachment.editors.codemirror4.attach(editor.codemirror, { ...imageUploadOptions, fileInputEl });

                // Floating add/remove row & column toolbar shown while the
                // caret is inside a markdown table.
                attachTableTools(editor);

                return [...editors, editor];
            },
            []
        );

        return fullMarkdownEditors;
    },
    addTargetBlankToExternalLinks() {
        let internal = location.host.replace("www.", "");
        internal = new RegExp(internal, "i");

        const links = [...document.getElementsByTagName("a")];
        links.forEach((link) => {
            if (internal.test(link.host)) return;

            link.setAttribute("target", "_blank");
        });
    },
    initializeImageZoom() {
        // Свой лайтбокс вместо lightense-images. Библиотека увеличивала
        // картинку "на месте", не давала кнопки закрытия и схлопывалась от
        // любого скролла — после просмотра было неудобно возвращаться к чтению.
        // Здесь обычная модалка: ✕ / Esc / клик по фону, скролл страницы под
        // ней заблокирован, а если оригинал крупнее экрана — клик по картинке
        // переключает масштаб 1:1 с прокруткой. Стили — .lightbox* в posts.css.
        //
        // Слушатель делегирован на document, а не навешен на каждую картинку:
        // так лайтбокс работает и для комментариев, дорисованных после загрузки
        // страницы (ответы, подгрузка тредов) — Lightense так не умел.
        let box = null;

        const close = () => {
            if (!box) return;
            box.classList.remove("is-open");
            document.body.classList.remove("lightbox-open");
            document.body.style.paddingRight = "";
            const el = box;
            box = null;
            // дать доиграть fade-out перед удалением из DOM
            setTimeout(() => el.remove(), 160);
        };

        const open = (img) => {
            close();

            // ширина скроллбара — чтобы страница под оверлеем не «прыгала»
            // вбок в момент, когда мы вешаем overflow: hidden на body
            const scrollbar = window.innerWidth - document.documentElement.clientWidth;

            const overlay = document.createElement("div");
            overlay.className = "lightbox";
            box = overlay;

            const figure = document.createElement("figure");
            figure.className = "lightbox-figure";

            const full = document.createElement("img");
            full.className = "lightbox-img";
            full.src = img.currentSrc || img.src;
            full.alt = img.alt || "";

            figure.appendChild(full);

            // подпись берём из figcaption рядом с картинкой, иначе из alt
            const figcaption = img.closest("figure") && img.closest("figure").querySelector("figcaption");
            const captionText = (figcaption && figcaption.textContent.trim()) || img.alt || "";
            if (captionText) {
                const caption = document.createElement("figcaption");
                caption.className = "lightbox-caption";
                caption.textContent = captionText;
                figure.appendChild(caption);
            }

            const closeButton = document.createElement("button");
            closeButton.type = "button";
            closeButton.className = "lightbox-close";
            closeButton.setAttribute("aria-label", "Закрити");
            closeButton.innerHTML = "&times;";
            closeButton.addEventListener("click", close);

            overlay.appendChild(closeButton);
            overlay.appendChild(figure);

            // клик по фону закрывает, клик по картинке/подписи — нет
            overlay.addEventListener("click", (e) => {
                if (e.target === overlay) close();
            });

            document.body.appendChild(overlay);
            document.body.classList.add("lightbox-open");
            if (scrollbar > 0) {
                document.body.style.paddingRight = `${scrollbar}px`;
            }

            // Масштаб 1:1 предлагаем только если оригинал реально крупнее того,
            // что поместилось на экран — иначе кнопка врала бы.
            full.addEventListener("load", () => {
                // Картинка могла догрузиться уже ПОСЛЕ того, как лайтбокс
                // закрыли (быстрый Esc по кешу браузера ещё не гарантирует
                // мгновенный load). Тогда overlay уже удалён из DOM, и трогать
                // его нельзя — раньше это падало с "Cannot read properties of
                // null (reading 'classList')".
                if (!overlay.isConnected) return;

                const fits = full.naturalWidth <= full.clientWidth + 1
                    && full.naturalHeight <= full.clientHeight + 1;
                if (fits) return;

                overlay.classList.add("is-zoomable");

                const hint = document.createElement("div");
                hint.className = "lightbox-hint";
                hint.textContent = `Клік — оригінал ${full.naturalWidth}×${full.naturalHeight}`;
                overlay.appendChild(hint);

                full.addEventListener("click", () => {
                    const actual = overlay.classList.toggle("is-actual");
                    hint.textContent = actual
                        ? "Клік — вписати в екран"
                        : `Клік — оригінал ${full.naturalWidth}×${full.naturalHeight}`;
                    if (!actual) overlay.scrollTo(0, 0);
                });
            });

            requestAnimationFrame(() => overlay.classList.add("is-open"));
        };

        document.addEventListener("click", (e) => {
            const img = e.target.closest(".text-body figure img");
            if (!img) return;
            // если автор обернул картинку в ссылку — уважаем ссылку
            if (img.closest("a")) return;
            e.preventDefault();
            open(img);
        });

        document.addEventListener("keydown", (e) => {
            if (e.key === "Escape" && box) close();
        });
    },
    blockCommunicationFormsResubmit() {
        [...document.querySelectorAll("form")].filter(isCommunicationForm).forEach((form) => {
            form.addEventListener("submit", () => {
                const submitButton = form.querySelector('button[type="submit"]');

                if (!submitButton) {
                    return;
                }

                submitButton.setAttribute("disabled", true);
            });
        });
    },
    restoreCommentThreadsState() {
        const comments = document.querySelectorAll(".reply, .comment");
        const collapsedSet = getCollapsedCommentThreadsSet();
        for (const comment of comments) {
            if (collapsedSet.has(comment.id)) {
                comment.querySelector(".comment-collapse-stub, .reply-collapse-stub").click();
            }
        }
    },
};

export default App;
